import React, { useEffect, useState } from "react";
import { Input, Button, Tooltip, message } from "antd";
import { AudioOutlined, AudioMutedOutlined } from "@ant-design/icons";
import SpeechRecognition, {
  useSpeechRecognition,
} from "react-speech-recognition";
import { useAIAssessmentContext } from "../../../../Context/AIAssessmentContext/AIAssessmentContext";
import useAiAssessmentQuestionNavigation from "../../hooks/useAiAssessmentQuestionNavigation";
import "./aiAssessmentAnswerInputs.scss";

const DescriptiveAnswerInput: React.FC = () => {
  const {
    fullscreenElementRef,
    isRoundFinished,
    currentStep,
    aiAssessmentQuestions,
  } = useAIAssessmentContext();

  const { saveTemporaryAnswer } = useAiAssessmentQuestionNavigation();

  const question = aiAssessmentQuestions[currentStep];
  
  message.config({
    getContainer: () =>
      fullscreenElementRef?.current
        ? fullscreenElementRef.current
        : document.body,
  });

  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isDisabled = isRoundFinished || question.answer !== null;

  const userAnswer = question.answer;

  const initialAnswer = isDisabled ? userAnswer : "";

  const [typedAnswer, setTypedAnswer] = useState<string>(initialAnswer ?? "");
  const [finalAnswer, setFinalAnswer] = useState<string>(initialAnswer ?? "");

  const handleBlockedAction = (e: any) => {
    e.preventDefault();
    message.warning("Copy, paste, and cut actions are disabled.");
  };

  const handleInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;

    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
      setTypedAnswer(value);
      setFinalAnswer(value);
      updateAnswerInContext(value);
      return;
    }

    setTypedAnswer(value);
    setFinalAnswer(value);
    updateAnswerInContext(value);
  };

  const handleOnInput = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const value = (e.target as HTMLTextAreaElement).value;

    if (listening) {
      SpeechRecognition.stopListening();
      resetTranscript();
      setTypedAnswer(value);
      setFinalAnswer(value);
      updateAnswerInContext(value);
      return;
    }

    setTypedAnswer(value);
    setFinalAnswer(value);
    updateAnswerInContext(value);
  };

  const handleFocus = () => {
    if (listening) {
      SpeechRecognition.stopListening();
      const merged = `${typedAnswer} ${transcript}`.trim();
      setTypedAnswer(merged);
      setFinalAnswer(merged);
      updateAnswerInContext(merged);
      resetTranscript();
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setTypedAnswer(value);
    setFinalAnswer(value);
    if (value.trim().length > 0) {
      updateAnswerInContext(value);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (listening && !e.ctrlKey && !e.altKey && !e.metaKey && !e.shiftKey) {
      const currentCombined = `${typedAnswer} ${transcript}`.trim();
      SpeechRecognition.stopListening();
      setTypedAnswer(currentCombined);
      setFinalAnswer(currentCombined);
      updateAnswerInContext(currentCombined);
      resetTranscript();
    }
  };

  const handleStartListening = () => {
    if (!browserSupportsSpeechRecognition) {
      message.error("Speech recognition not supported in this browser.");
      return;
    }

    try {
      resetTranscript();
      SpeechRecognition.startListening({
        continuous: true,
        language: "en-US",
        interimResults: true,
      });
    } catch (error) {
      console.error("Error starting speech recognition:", error);
      message.error("Failed to start speech recognition. Please try again.");
    }
  };

  const handleStopListening = () => {
    SpeechRecognition.stopListening();
    const merged = `${typedAnswer} ${transcript}`.trim();
    setTypedAnswer(merged);
    setFinalAnswer(merged);
    updateAnswerInContext(merged);
    resetTranscript();
  };

  const updateAnswerInContext = (value: string) => {
    if(value.trim().length <= 0){
      saveTemporaryAnswer(null);
      return;
    } else {
      saveTemporaryAnswer(value);
      return;
    }
  };

  useEffect(() => {
    SpeechRecognition.stopListening();
    resetTranscript();
    setTypedAnswer(initialAnswer ?? "");
    setFinalAnswer(initialAnswer ?? "");
    return () => {
      SpeechRecognition.stopListening();
      resetTranscript();
    };
  }, [question?.questionId]);

  const combinedAnswer = listening
    ? `${typedAnswer} ${transcript}`.trim()
    : finalAnswer;

  useEffect(() => {
    if (listening) {
      updateAnswerInContext(combinedAnswer);
    }
  }, [listening, combinedAnswer]);

  return (
    <div
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
      }}
    >
      {!isDisabled && !isSafari && (
        <div
          style={{
            marginBottom: 8,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Tooltip title={listening ? "Stop listening" : "Speak your answer"}>
            <Button
              type="primary"
              icon={listening ? <AudioMutedOutlined /> : <AudioOutlined />}
              onClick={listening ? handleStopListening : handleStartListening}
              size="small"
            >
              {listening ? "Listening..." : "Speak"}
            </Button>
          </Tooltip>
        </div>
      )}

      <Input.TextArea
        autoSize={{ minRows: 6, maxRows: 6 }}
        placeholder={
          isRoundFinished ? "No answer provided" : "Type your answer here"
        }
        value={combinedAnswer}
        onChange={handleInput}
        onInput={handleOnInput}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        onCopy={handleBlockedAction}
        onPaste={handleBlockedAction}
        onCut={handleBlockedAction}
        style={{
          userSelect: "text",
          WebkitUserSelect: "text",
          MozUserSelect: "text",
          msUserSelect: "text",
        }}
      />
    </div>
  );
};

export default DescriptiveAnswerInput;
